import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {HelpIcon} from '@/components/help-icon'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  MATRIZ_CARGOS_PAGE_SIZE,
  useIgnorarAviso,
  useMatrizCargos,
  useVolverAMostrarAviso,
  type CargoDeFila,
  type FilaCargos,
} from '@/hooks/sgsst/riesgos/use-matriz-cargos'
import {translateApiError} from '@/lib/errors'
import {SearchInput} from '@/components/ui/search-input'
import {tope} from '@/lib/campos-texto'

/** Con la versión: sin ella, dos lecturas en días distintos no saben si ven el mismo perfil. */
function CargoChip({businessId, cargo}: {businessId: string; cargo: CargoDeFila}) {
  const {t} = useTranslation()
  return (
    <Link
      to={`/businesses/${businessId}/sgsst/perfiles-cargo/${cargo.position_id}`}
      className="inline-flex items-center gap-1.5 rounded-[3px] bg-secondary px-1.5 py-[3px] text-xs text-foreground hover:underline"
      data-testid="matriz-cargos-chip">
      <span>{cargo.cargo}</span>
      <span className="font-mono text-[10px] text-muted-foreground">
        {t('matrizCargos.version', {version: cargo.version})}
      </span>
    </Link>
  )
}

/** Vacía dice «sin definir», nunca «ninguno»: el vacío no es una afirmación (S-2). */
function CeldaCargos({businessId, fila}: {businessId: string; fila: FilaCargos}) {
  const {t} = useTranslation()
  if (fila.cargos.length === 0) {
    return (
      <span className="text-sm text-muted-foreground" data-testid="matriz-cargos-sin-definir">
        {t('matrizCargos.sinDefinir')}
      </span>
    )
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {fila.cargos.map((cargo) => (
        <CargoChip key={cargo.position_id} businessId={businessId} cargo={cargo} />
      ))}
    </div>
  )
}

/** Un enlace por cargo: un botón único llevaría al primero y dejaría creer resuelto el aviso entero. */
function Pendientes({businessId, fila}: {businessId: string; fila: FilaCargos}) {
  const {t} = useTranslation()
  return (
    <>
      <ul className="space-y-1">
        {fila.pendientes.map((cargo) => (
          <li key={cargo.position_id} className="flex flex-wrap items-center gap-2">
            <CargoChip businessId={businessId} cargo={cargo} />
            {cargo.declarante_nombre && (
              <span data-testid="matriz-cargos-diferencia">
                {t('matrizCargos.aviso.diferencia', {nombre: cargo.declarante_nombre})}
              </span>
            )}
            <Link
              to={`/businesses/${businessId}/sgsst/perfiles-cargo/${cargo.position_id}`}
              className="underline"
              data-testid="matriz-cargos-abrir-perfil">
              {t('matrizCargos.aviso.agregarEn', {cargo: cargo.cargo})}
            </Link>
          </li>
        ))}
      </ul>
      {fila.pendientes.length > 1 && <p>{t('matrizCargos.aviso.agregarCadaUno')}</p>}
    </>
  )
}

/** La Matriz nunca escribe el perfil. «Ignorar» es acto de firmante (`require_firmante`): se
 * pinta siempre, deshabilitado con su razón cuando quien mira no puede firmar. */
function AvisoDeLaFila({
  businessId,
  fila,
  puedeIgnorar,
  bloqueoFirma,
  onIgnorar,
}: {
  businessId: string
  fila: FilaCargos
  puedeIgnorar: boolean
  bloqueoFirma: string | null
  onIgnorar: () => void
}) {
  const {t} = useTranslation()
  const reaparicion = fila.aviso?.reaparicion
  return (
    <div className="notice-amber space-y-2 rounded-lg px-3.5 py-2.5 text-xs" data-testid="matriz-cargos-aviso">
      {reaparicion && (
        <p data-testid="matriz-cargos-aviso-reaparicion">
          {t(`matrizCargos.aviso.vuelve.${reaparicion.cambio}`, {
            numero: fila.numero_fila,
            nivelAnterior: reaparicion.nivel_anterior ?? t('matrizCargos.sinNivel'),
            nivelActual: fila.nivel_riesgo ?? t('matrizCargos.sinNivel'),
            peligroAnterior: reaparicion.peligro_anterior,
            fecha: new Date(reaparicion.ignorado_en).toLocaleDateString('es-CO'),
            nombre: reaparicion.ignorado_por_nombre ?? t('matrizCargos.aviso.alguien'),
          })}
        </p>
      )}
      <Pendientes businessId={businessId} fila={fila} />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!puedeIgnorar}
          onClick={onIgnorar}
          data-testid="matriz-cargos-ignorar">
          {t('matrizCargos.aviso.ignorar')}
        </Button>
        {!puedeIgnorar && bloqueoFirma && (
          <span className="text-muted-foreground" data-testid="matriz-cargos-firma-bloqueada">
            {bloqueoFirma}
          </span>
        )}
      </div>
    </div>
  )
}

/** Sin este bloque una fila silenciada sería idéntica a una que nadie miró, y su motivo no se
 * leería en ninguna superficie. */
function SilencioDeLaFila({
  businessId,
  fila,
  puedeIgnorar,
  bloqueoFirma,
  onVolverAMostrar,
  pending,
}: {
  businessId: string
  fila: FilaCargos
  puedeIgnorar: boolean
  bloqueoFirma: string | null
  onVolverAMostrar: () => void
  pending: boolean
}) {
  const {t} = useTranslation()
  const silenciado = fila.silenciado
  if (!silenciado) return null
  return (
    <div
      className="space-y-2 rounded-lg border bg-muted/40 px-3.5 py-2.5 text-xs text-muted-foreground"
      data-testid="matriz-cargos-silenciado">
      <p>
        {t('matrizCargos.silenciado.quien', {
          nombre: silenciado.ignorado_por_nombre ?? t('matrizCargos.aviso.alguien'),
          fecha: new Date(silenciado.ignorado_en).toLocaleDateString('es-CO'),
          motivo: silenciado.motivo,
        })}
      </p>
      <p>{t('matrizCargos.silenciado.vuelve')}</p>
      <Pendientes businessId={businessId} fila={fila} />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!puedeIgnorar || pending}
          onClick={onVolverAMostrar}
          data-testid="matriz-cargos-volver-a-mostrar">
          {t('matrizCargos.silenciado.mostrar')}
        </Button>
        {!puedeIgnorar && bloqueoFirma && <span data-testid="matriz-cargos-firma-bloqueada">{bloqueoFirma}</span>}
      </div>
    </div>
  )
}

/** Sin formulario HTML a propósito, como `ipevr/descarte-dialogs.tsx`: la única validación local es
 * inhabilitar el botón con el motivo vacío; el blanco lo rechaza el contrato. */
function IgnorarDialog({
  open,
  onOpenChange,
  numero,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  numero: string
  pending: boolean
  error: string | null
  onConfirm: (motivo: string) => void
}) {
  const {t} = useTranslation()
  const [motivo, setMotivo] = useState('')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="matriz-cargos-ignorar-dialog">
        <DialogHeader>
          <DialogTitle>{t('matrizCargos.ignorar.title', {numero})}</DialogTitle>
          <DialogDescription>{t('matrizCargos.ignorar.body')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('matrizCargos.ignorar.motivoLabel')}
          </p>
          <Textarea
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
            placeholder={t('matrizCargos.ignorar.motivoPlaceholder')}
            maxLength={tope('NOTA')}
            data-testid="matriz-cargos-motivo"
          />
        </div>
        {error && (
          <p className="text-xs text-destructive" data-testid="matriz-cargos-ignorar-error">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" size="sm" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={pending || motivo.trim().length === 0}
            onClick={() => onConfirm(motivo.trim())}
            data-testid="matriz-cargos-ignorar-confirmar">
            {t('matrizCargos.ignorar.confirmar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** En su propia pestaña, fuera de la rejilla editable: la columna es derivada y el perfil se
 * cambia en el perfil (E1-H02). */
export function MatrizColumnaCargos({
  businessId,
  active,
  puedeIgnorar,
  bloqueoFirma,
}: {
  businessId: string
  active: boolean
  puedeIgnorar: boolean
  bloqueoFirma: string | null
}) {
  const {t} = useTranslation()
  const table = useTableState('numero_fila')
  const [soloConAviso, setSoloConAviso] = useState(false)
  const [ignorando, setIgnorando] = useState<FilaCargos | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorAccion, setErrorAccion] = useState<string | null>(null)
  // Sin `sortBy`/`sortDir`: el backend no los honra y siempre ordena por numeración.
  const lista = useMatrizCargos(
    businessId,
    {page: table.page, search: table.search, extra: {solo_con_aviso: soloConAviso ? 'true' : undefined}},
    active
  )
  const ignorar = useIgnorarAviso(businessId)
  const volverAMostrar = useVolverAMostrarAviso(businessId)

  // Conteos de la misma lectura y no del `total` del pie, que cambia con el filtro puesto;
  // `undefined` mientras carga pinta el rótulo pelado en vez de un «(0)» que salta.
  const opciones: OpcionEstado<'todas' | 'con-aviso'>[] = [
    {valor: 'todas', etiqueta: t('matrizCargos.filtro.todas'), conteo: lista.data?.total_todas},
    {valor: 'con-aviso', etiqueta: t('matrizCargos.filtro.conAviso'), conteo: lista.data?.total_con_aviso},
  ]

  const columnas: Column<FilaCargos>[] = [
    {
      key: 'numero_fila',
      header: t('matrizCargos.col.fila'),
      width: 'w-16',
      cell: (fila) => <span className="font-mono text-xs">{fila.numero_fila}</span>,
    },
    {
      key: 'descripcion',
      header: t('matrizCargos.col.peligro'),
      width: 'w-[32%]',
      cell: (fila) => (
        <CeldaEstado
          chip={
            fila.aviso ? (
              <Tag tone="amber" data-testid="matriz-cargos-chip-aviso">
                {t('matrizCargos.chip.conAviso')}
              </Tag>
            ) : fila.silenciado ? (
              <Tag tone="neutral" data-testid="matriz-cargos-chip-silenciado">
                {t('matrizCargos.chip.silenciado')}
              </Tag>
            ) : undefined
          }
          nombre={<span className="font-normal break-words">{fila.descripcion}</span>}
          meta={
            <span className="flex flex-wrap items-center gap-1.5">
              <Tag>{t(`ipevr.clasificacion.${fila.clasificacion}`)}</Tag>
              {fila.area_nombre && <Tag>{fila.area_nombre}</Tag>}
              {!fila.escala_comparable && (
                <Tag tone="amber" data-testid="matriz-cargos-escala-vieja">
                  {t('matrizCargos.escalaVieja')}
                </Tag>
              )}
            </span>
          }
          porque={fila.aviso ? t('matrizCargos.aviso.cuerpo', {count: fila.pendientes.length}) : undefined}
        />
      ),
    },
    {
      key: 'cargos',
      header: t('matrizCargos.col.cargos'),
      headerAside: (
        <HelpIcon label={t('matrizCargos.introTipLabel')} testId="matriz-cargos-help">
          {t('matrizCargos.intro')}
        </HelpIcon>
      ),
      cell: (fila) => <CeldaCargos businessId={businessId} fila={fila} />,
    },
  ]

  return (
    <div className="space-y-3" data-testid="matriz-columna-cargos">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={table.searchInput}
          onChange={table.setSearchInput}
          placeholder={t('matrizCargos.buscar')}
          className="max-w-sm"
          data-testid="matriz-cargos-buscar"
        />
        <FiltrosDeEstado
          opciones={opciones}
          valor={soloConAviso ? 'con-aviso' : 'todas'}
          onValor={(valor) => {
            setSoloConAviso(valor === 'con-aviso')
            table.setPage(1)
          }}
          testIdPrefijo="matriz-cargos-solo"
        />
      </div>
      {errorAccion && (
        <p className="text-sm text-destructive" data-testid="matriz-cargos-error-accion">
          {errorAccion}
        </p>
      )}
      {lista.isError && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="matriz-cargos-error-carga">
          {t('matrizCargos.errorCarga')}{' '}
          <Button size="sm" variant="outline" onClick={() => void lista.refetch()}>
            {t('matrizCargos.reintentar')}
          </Button>
        </p>
      )}
      {!lista.data && !lista.isError && <Skeleton className="h-40 w-full" />}
      {lista.data && (
        <>
          <DataTable
            columns={columnas}
            rows={lista.data.items}
            rowKey={(fila) => fila.riesgo_fila_id}
            emptyState={
              table.search ? (
                <div className="space-y-2 text-sm text-muted-foreground" data-testid="matriz-cargos-vacio-busqueda">
                  <p>{t('matrizCargos.vacioBusqueda', {termino: table.search})}</p>
                  <Button size="sm" variant="outline" onClick={() => table.setSearchInput('')}>
                    {t('matrizCargos.limpiarBusqueda')}
                  </Button>
                </div>
              ) : (
                <span data-testid="matriz-cargos-vacio">{t('matrizCargos.vacio')}</span>
              )
            }
            renderRowDetail={(fila) =>
              fila.aviso ? (
                <AvisoDeLaFila
                  businessId={businessId}
                  fila={fila}
                  puedeIgnorar={puedeIgnorar}
                  bloqueoFirma={bloqueoFirma}
                  onIgnorar={() => {
                    setError(null)
                    setIgnorando(fila)
                  }}
                />
              ) : fila.silenciado ? (
                <SilencioDeLaFila
                  businessId={businessId}
                  fila={fila}
                  puedeIgnorar={puedeIgnorar}
                  bloqueoFirma={bloqueoFirma}
                  pending={volverAMostrar.isPending}
                  onVolverAMostrar={() => {
                    setErrorAccion(null)
                    volverAMostrar.mutate(
                      {riesgoFilaId: fila.riesgo_fila_id},
                      {onError: (err) => setErrorAccion(translateApiError(err))}
                    )
                  }}
                />
              ) : null
            }
          />
          <PaginationBar
            page={table.page}
            pageSize={MATRIZ_CARGOS_PAGE_SIZE}
            total={lista.data.total}
            onPageChange={table.setPage}
          />
        </>
      )}
      {/* `key` por fila: el diálogo vive fuera de la tabla y sin remontarlo el `motivo`
          de una fila sobreviviría a la siguiente. */}
      <IgnorarDialog
        key={ignorando?.riesgo_fila_id ?? 'cerrado'}
        open={ignorando !== null}
        onOpenChange={(next) => !next && setIgnorando(null)}
        numero={ignorando?.numero_fila ?? ''}
        pending={ignorar.isPending}
        error={error}
        onConfirm={(motivo) => {
          if (!ignorando) return
          ignorar.mutate(
            {riesgoFilaId: ignorando.riesgo_fila_id, motivo},
            {
              onSuccess: () => setIgnorando(null),
              onError: (err) => setError(translateApiError(err)),
            }
          )
        }}
      />
    </div>
  )
}
